import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnChanges,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * One-box-per-digit code input (verification codes, TOTP). Form-bindable via `formControlName`;
 * the value is the concatenated digits. Emits `completed` once every box holds a digit.
 */
@Component({
    selector: 'p-otp-input',
    templateUrl: './p-otp-input.component.html',
    styleUrls: ['./p-otp-input.component.scss'],
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => POtpInputComponent), multi: true }],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class POtpInputComponent implements ControlValueAccessor, OnChanges {
    @Input() length = 6;
    @Input() hasError = false;
    @Output() completed = new EventEmitter<string>();

    @ViewChildren('box') private boxes!: QueryList<ElementRef<HTMLInputElement>>;

    digits: string[] = this.emptyDigits(6);
    isDisabled = false;

    private onChange: (value: string) => void = () => undefined;
    onTouched: () => void = () => undefined;

    constructor(private readonly cd: ChangeDetectorRef) {}

    get indexes(): number[] {
        return this.digits.map((_, i) => i);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['length']) {
            this.digits = this.emptyDigits(this.length);
        }
    }

    trackByIndex(index: number): number {
        return index;
    }

    // ---- ControlValueAccessor ----

    writeValue(value: string | null): void {
        this.digits = this.emptyDigits(this.length);
        this.onlyDigits(value ?? '')
            .slice(0, this.length)
            .split('')
            .forEach((d, i) => (this.digits[i] = d));
        this.cd.markForCheck();
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
        this.cd.markForCheck();
    }

    // ---- interaction ----

    onInput(index: number, event: Event): void {
        const input = event.target as HTMLInputElement;
        const typed = this.onlyDigits(input.value);
        const digit = typed.slice(-1);
        input.value = digit;
        if (!digit) {
            this.setDigit(index, '');
            return;
        }
        this.setDigit(index, digit);
        this.focusBox(index + 1);
    }

    onKeydown(index: number, event: KeyboardEvent): void {
        if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
            event.preventDefault();
            this.setDigit(index - 1, '');
            this.focusBox(index - 1);
        } else if (event.key === 'ArrowLeft') {
            this.focusBox(index - 1);
        } else if (event.key === 'ArrowRight') {
            this.focusBox(index + 1);
        }
    }

    onPaste(event: ClipboardEvent): void {
        const pasted = this.onlyDigits(event.clipboardData?.getData('text') ?? '').slice(0, this.length);
        if (!pasted) {
            return;
        }
        event.preventDefault();
        this.digits = this.emptyDigits(this.length);
        pasted.split('').forEach((d, i) => (this.digits[i] = d));
        this.syncBoxes();
        this.emitValue();
        this.focusBox(Math.min(pasted.length, this.length - 1));
    }

    // ---- internals ----

    private setDigit(index: number, digit: string): void {
        this.digits = this.digits.map((d, i) => (i === index ? digit : d));
        this.syncBoxes();
        this.emitValue();
    }

    private emitValue(): void {
        const value = this.digits.join('');
        this.onChange(value);
        if (value.length === this.length) {
            this.completed.emit(value);
        }
        this.cd.markForCheck();
    }

    private syncBoxes(): void {
        this.boxes?.forEach((box, i) => (box.nativeElement.value = this.digits[i]));
    }

    private focusBox(index: number): void {
        const box = this.boxes?.get(index);
        box?.nativeElement.focus();
        box?.nativeElement.select?.();
    }

    private onlyDigits(value: string): string {
        return value.replace(/\D/g, '');
    }

    private emptyDigits(length: number): string[] {
        return Array.from({ length: Math.max(1, length) }, () => '');
    }
}
